import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Row, Col, Alert } from "react-bootstrap";
import { updateAlertSettings } from "../../store/alert/actions/actionsCreators";
import "./AlertSettingsForm.css";

const AlertSettingsForm = ({ initialSettings }) => {
  const dispatch = useDispatch();
  const { updating } = useSelector((state) => state.alert);
  const { user } = useSelector((state) => state.user);
  const token = user?.access_token;

  const [formData, setFormData] = useState({
    isActive: false,
    message: "",
    alertType: "INFO"
  });

  useEffect(() => {
    if (initialSettings) {
      setFormData({
        isActive: initialSettings.isActive || false,
        message: initialSettings.message || "",
        alertType: initialSettings.alertType || "INFO"
      });
    }
  }, [initialSettings]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.message.trim()) {
      return;
    }

    dispatch(updateAlertSettings(formData, token));
  };

  const getAlertIcon = () => {
    switch (formData.alertType) {
      case "WARNING": return "⚠️";
      case "MAINTENANCE": return "🔧";
      case "URGENT": return "🚨";
      default: return "ℹ️";
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-4">
        <Col md={12}>
          <div className="d-flex align-items-center">
            <Form.Check
              type="switch"
              id="isActive"
              name="isActive"
              checked={formData.isActive}
              onChange={handleChange}
              className="me-3"
              style={{ transform: "scale(1.2)" }}
            />
            <div>
              <strong className={formData.isActive ? "text-success" : "text-muted"}>
                {formData.isActive ? "🟢 Alert Active" : "⚪ Alert Inactive"}
              </strong>
              <br />
              <small className="text-muted">
                {formData.isActive 
                  ? "Alert is visible to all users" 
                  : "Alert is hidden from users"}
              </small>
            </div>
          </div>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              <strong>Alert Type</strong>
            </Form.Label>
            <Form.Select
              name="alertType"
              value={formData.alertType}
              onChange={handleChange}
              required
            >
              <option value="INFO">ℹ️ Info - General information</option>
              <option value="WARNING">⚠️ Warning - Important notice</option>
              <option value="MAINTENANCE">🔧 Maintenance - System updates</option>
              <option value="URGENT">🚨 Urgent - Critical alert</option>
            </Form.Select>
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>
              <strong>Alert Message</strong>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Enter your alert message here... (e.g., 'System maintenance scheduled for tonight at 10 PM EST')"
              required
            />
            <div className="d-flex justify-content-between mt-1">
              <Form.Text className="text-muted">
                This message will appear at the top of all pages when the alert is active
              </Form.Text>
              <Form.Text className={formData.message.length > 200 ? "text-warning" : "text-muted"}>
                {formData.message.length} characters
                {formData.message.length > 200 && " (Keep it concise)"}
              </Form.Text>
            </div>
          </Form.Group>
        </Col>
      </Row>

      {formData.message && (
        <Row className="mb-4">
          <Col md={12}>
            <div className="preview-section">
              <h6 className="mb-2">
                <strong>Preview</strong>
              </h6>
              {formData.isActive ? (
                <div className={`alert-preview alert-${formData.alertType.toLowerCase()}`}>
                  <span className="me-2">{getAlertIcon()}</span>
                  {formData.message}
                </div>
              ) : (
                <div className="alert alert-secondary">
                  <em>Preview only available when alert is active</em>
                </div>
              )}
            </div>
          </Col>
        </Row>
      )}

      <Row>
        <Col md={12}>
          <Button
            variant="primary"
            type="submit"
            disabled={updating || !formData.message.trim()}
            className="px-4"
          >
            {updating ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Saving...
              </>
            ) : (
              "💾 Save Alert Settings"
            )}
          </Button>
          {!formData.message.trim() && (
            <small className="text-muted ms-3">
              Please enter a message to save
            </small>
          )}
        </Col>
      </Row>

      <Row className="mt-4">
        <Col md={12}>
          <Alert variant="info">
            <Alert.Heading>Tips for Effective Alerts</Alert.Heading>
            <ul className="mb-0">
              <li><strong>Keep it short:</strong> Users should understand the message at a glance</li>
              <li><strong>Be specific:</strong> Include dates, times, or affected services</li>
              <li><strong>Choose the right type:</strong> Use URGENT sparingly for critical issues only</li>
              <li><strong>Update regularly:</strong> Remove alerts when the issue is resolved</li>
            </ul>
          </Alert>
        </Col>
      </Row>
    </Form>
  );
};

export default AlertSettingsForm;